import { MessageTooltip, pxToRem, useTheme } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import type {
    Position,
    TagDataUseToCreateTag,
    TagType,
} from '~/modules/product-tagging/type';
import * as S from './TagWithModals.style';

import useModalPosition from '../../hooks/use-modal-position';
import { useProductTagging } from '../../context';
import {
    TaggingModalShell,
    TaggingModalStaticHeader,
    TaggingModalFooterButton,
    TaggingModalFooterButtonOutline,
} from '@modules/product-tagging/components/tagging-modal-shell/TaggingModalShell';
import ProductTaggingSearchModal from '@modules/product-tagging/components/product-tagging-search/ProductTaggingSearchModal';
import SupplierDetailsForm from '@modules/product-tagging/components/supplier-details-form/SupplierDetailsForm';
import { useTagModals } from './hook/use-tag-modals';
import TagMarker from '@modules/product-tagging/components/tag-with-modals/TagMarker';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    PRODUCT_TAG_WITH_MODAL_WIDTH,
    PRODUCT_TAGGING_MODAL_OFFSET_FROM_TAG,
} from '@modules/product-tagging/constant';
import useModalPlacement from '@modules/product-tagging/hooks/use-modal-placement';
import useEditTag from '@modules/product-tagging/hooks/use-edit-tag';
import HelpGuideStepTwo from './HelpGuideStepTwo';
import { useModulesContext } from '~/modules/page-builder/context/modules-context';
import { useUser } from '~/modules/user';
import { useHelpState } from '../../hooks/use-help-state';
import { getScrollParams } from '~/modules/page-builder/util/get-scroll-params';
import { TagHoverContent } from './TagHoverContent';

interface TagProps {
    marker: TagType;
    tagPosition: Position;
    boxWidth: number;
    showImageOverlay?: boolean;
    boxRef: Element;
    deleteTag: (tagId: number) => void;
    onTagUpdated?: (tagId: number, approved: boolean) => void;
}

const TagWithModals = ({
    marker,
    tagPosition,
    boxWidth,
    showImageOverlay = false,
    boxRef,
    deleteTag,
    onTagUpdated,
}: TagProps) => {
    const {
        state: { isDeletingTag, isAddingSupplier },
        dispatch,
        canDeleteTag,
    } = useProductTagging();

    const {
        state: { showApproveTagHelp, approveTagHelpTagID },
        dispatch: modulesContextDispatch,
    } = useModulesContext();

    const user = useUser();
    const { addMember } = useHelpState();

    const { toast } = useToaster();

    const [isEditing, setIsEditing] = useState(false);
    const forceCloseRef = useRef<() => void>();

    const onEditComplete = useCallback(() => {
        setIsEditing(false);
        forceCloseRef.current?.();
        dispatch({ type: 'TOGGLE_ADDING_SUPPLIER', payload: false });
    }, [dispatch]);

    const { editTag } = useEditTag(onEditComplete);

    const handleEditMarker = useCallback(
        (data: TagDataUseToCreateTag) => {
            editTag(marker.id, data);
        },
        [editTag, marker.id],
    );

    const tagId = marker.id;
    const onApproveClicked = () => {
        if (canShowProductTile) {
            addMember(user);
        }
    };
    const onDeleteClicked = () => {
        if (canShowProductTile) {
            addMember(user);
        }
    };

    const {
        isModalOpen,
        isMarkerApproved,
        isCompletedTogglingApproved,
        showModal,
        canShowProductTile,
        bottomActions,
        confirmationMessage,
        onDeleteTag,
        setIsOpen,
        forceClose,
        hasProduct,
    } = useTagModals({ marker, deleteTag, onApproveClicked, onDeleteClicked });
    forceCloseRef.current = forceClose;

    const handleDismiss = useCallback(() => {
        setIsOpen(false);
        if (isAddingSupplier) {
            dispatch({ type: 'TOGGLE_ADDING_SUPPLIER', payload: false });
        }
    }, [setIsOpen, isAddingSupplier, dispatch]);

    const handleEditDismiss = useCallback(() => {
        setIsEditing(false);
        forceClose();
        dispatch({ type: 'TOGGLE_ADDING_SUPPLIER', payload: false });
    }, [forceClose, dispatch]);

    const placement = useModalPlacement(
        tagPosition,
        PRODUCT_TAG_WITH_MODAL_WIDTH + PRODUCT_TAGGING_MODAL_OFFSET_FROM_TAG,
        boxWidth,
    );

    const {
        setReferenceElement,
        setPopperElement,
        styles,
        attributes,
        update,
    } = useModalPosition(boxRef, placement);

    // Each time a new modal opens, update the position of the popper
    useEffect(() => {
        if (isModalOpen && update) {
            update();
        }
    }, [update, isModalOpen]);

    const { css } = useStyles({ showHover: isModalOpen });
    const theme = useTheme();

    const product = marker.product;

    let isShowingHelp = showApproveTagHelp && marker.id === approveTagHelpTagID;

    const { scrollToTagID } = getScrollParams(
        window?.top?.location?.search ?? '',
    );

    const openTagByDefault =
        !isShowingHelp && scrollToTagID === marker.id ? true : false;

    let tagVisibility = {};
    if (isShowingHelp === true || openTagByDefault) {
        tagVisibility = {
            visibility: 'visible',
            opacity: 1,
        };
    }

    // if openTagByDefault is true, then display modal when page loads
    useEffect(() => {
        if (openTagByDefault) {
            setIsOpen(true);
        }
    }, [openTagByDefault, setIsOpen]);

    useEffect(() => {
        if (!isCompletedTogglingApproved) {
            return;
        }
        onTagUpdated?.(tagId, isMarkerApproved);
        toast(isMarkerApproved ? 'Tag approved' : 'Tag un-approved');
    }, [
        isCompletedTogglingApproved,
        isMarkerApproved,
        tagId,
        toast,
        onTagUpdated,
    ]);

    return (
        <div
            style={{
                top: `${tagPosition.y}%`,
                left: `${tagPosition.x}%`,
                ...tagVisibility,
            }}
            className={`${css(S.TagModalsContainer)} product-tagging-tag`}
            ref={setReferenceElement}
            onMouseEnter={() => {
                // we don't want to show the modal if the help guide is open
                if (!isShowingHelp) {
                    setIsOpen(true);
                }
            }}
            onMouseLeave={() => {
                if (!isEditing) {
                    setIsOpen(false);
                }
            }}
        >
            <MessageTooltip
                open={isShowingHelp}
                trigger={
                    <TagMarker
                        isApproved={isMarkerApproved}
                        isActive={isModalOpen}
                    />
                }
                content={{
                    children: (
                        <HelpGuideStepTwo
                            onClose={() => {
                                addMember(user);
                                modulesContextDispatch({
                                    type: 'resetApproveTagHelp',
                                });
                            }}
                        />
                    ),
                    color: 'white',
                    padding: 0,
                    variables: {
                        maxWidth: pxToRem(400),
                    },
                    className: css(
                        tagPosition.x <= 50
                            ? S.TooltipContentAfter
                            : S.TooltipContentBefore,
                    ),
                }}
                position={tagPosition.x <= 50 ? 'after' : 'before'}
                offset={[0, 12]}
                pointing={true}
            />
            {isModalOpen &&
                canShowProductTile &&
                !isShowingHelp &&
                product?.id && (
                    <div
                        ref={setPopperElement}
                        style={styles.popper}
                        {...attributes.popper}
                        className={css(S.ProductTagTileContainer)}
                    >
                        <TagHoverContent
                            bottomActions={bottomActions}
                            showImageOverlay={showImageOverlay}
                            marker={marker}
                        />
                    </div>
                )}
            {(showModal || isEditing) && (
                <div
                    style={{
                        ...styles.popper,
                        zIndex: theme.siteVariables.zIndexes.overlay,
                    }}
                    ref={setPopperElement}
                    {...attributes.popper}
                >
                    {isEditing ? (
                        isAddingSupplier ? (
                            <SupplierDetailsForm
                                onSubmit={handleEditMarker}
                                onDismiss={handleEditDismiss}
                            />
                        ) : (
                            <ProductTaggingSearchModal
                                onCreateMarker={handleEditMarker}
                                popperState={null}
                                onDismiss={handleEditDismiss}
                            />
                        )
                    ) : (
                        <TaggingModalShell onDismiss={handleDismiss}>
                            <TaggingModalStaticHeader
                                title={
                                    hasProduct
                                        ? 'Tag Saved'
                                        : 'Awaiting confirmation'
                                }
                                message={confirmationMessage}
                                onClose={handleDismiss}
                            />
                            {canDeleteTag && (
                                <TaggingModalFooterButton
                                    label="Replace Tag"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEditing(true);
                                    }}
                                />
                            )}
                            {canDeleteTag ? (
                                <TaggingModalFooterButtonOutline
                                    label="Remove Tag"
                                    onClick={onDeleteTag}
                                    disabled={isDeletingTag}
                                />
                            ) : (
                                <TaggingModalFooterButton
                                    label="Remove Tag"
                                    onClick={onDeleteTag}
                                    disabled={isDeletingTag}
                                />
                            )}
                        </TaggingModalShell>
                    )}
                </div>
            )}
        </div>
    );
};

export default TagWithModals;
